import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { usePlayerTeam, useLeaveTeam } from '@/hooks/use-coach';
import { EditableUserName } from '@/components/editable-user-name';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Users,
  User,
  Mail,
  Calendar,
  LogOut,
  CreditCard,
  Settings as SettingsIcon,
  Shield,
} from 'lucide-react';

export default function SettingsPage() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [, setLocation] = useLocation();

  const { data: teamData, isLoading: teamLoading } = usePlayerTeam();
  const leaveTeamMutation = useLeaveTeam();

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isAuthenticated, authLoading, setLocation]);

  if (authLoading || teamLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLeaveTeam = () => {
    leaveTeamMutation.mutate(undefined, {
      onSuccess: () => {
        setShowLeaveConfirm(false);
      },
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  const isCoach = user.accountType === 'coach';
  const hasTeam = teamData?.hasTeam;

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <Link href="/">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Settings</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage your account and preferences.
            </p>
          </div>

          <div className="space-y-6">
            {/* Account Info Card */}
            <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Account
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] flex items-center justify-center text-white text-xl font-bold">
                    {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                  </div>
                  <div>
                    <EditableUserName currentName={user.name || ''} />
                    <p className="text-gray-500 flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </p>
                    <p className="text-sm text-gray-400 capitalize flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      {user.accountType || 'player'} account
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Section - Only for Players */}
            {!isCoach && (
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Team
                  </CardTitle>
                  <CardDescription>
                    {hasTeam
                      ? 'Your current team membership'
                      : 'Join a team to access shared credits and coach feedback'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {hasTeam && teamData.team ? (
                    <div className="space-y-4">
                      <div className="glass-card dark:glass-card-dark rounded-2xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-lg">{teamData.team.name}</p>
                            {teamData.coach && (
                              <p className="text-sm text-gray-500">
                                Coach: {teamData.coach.name || teamData.coach.email}
                              </p>
                            )}
                            {teamData.membership && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                <Calendar className="w-3 h-3" />
                                Joined {formatDate(teamData.membership.joinedAt as unknown as string)}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-[var(--apple-green)]">
                              {teamData.team.teamCredits}
                            </p>
                            <p className="text-xs text-gray-500">team credits</p>
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => setShowLeaveConfirm(true)}
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Leave Team
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        You're not currently on a team.
                      </p>
                      <Link href="/join-team">
                        <Button className="btn-primary">
                          Join a Team
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Coach Dashboard Link - Only for Coaches */}
            {isCoach && (
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <SettingsIcon className="w-5 h-5" />
                    Coach Tools
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Link href="/coach/dashboard">
                    <Button className="w-full btn-primary">
                      <Users className="w-4 h-4 mr-2" />
                      Go to Coach Dashboard
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Subscription Card */}
            <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Subscription
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">
                      {user.planTier === 'free' && 'Free Plan'}
                      {user.planTier === 'pro_starter' && 'Pro Starter Plan'}
                      {user.planTier === 'elite_pro' && 'Elite Pro Plan'}
                      {user.planTier === 'ultra_team' && 'Ultra Team Plan'}
                      {!user.planTier && 'Free Plan'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {user.credits} credits remaining
                    </p>
                  </div>
                  <Link href="/pricing">
                    <Button variant="outline">
                      {user.planTier === 'free' ? 'Upgrade' : 'Manage'}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Leave Team Confirmation */}
      <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <AlertDialogContent className="glass-card dark:glass-card-dark rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Team?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave {teamData?.team?.name}? You'll lose access to team credits,
              but you can rejoin later using a new invite code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600 rounded-2xl"
              onClick={handleLeaveTeam}
              disabled={leaveTeamMutation.isPending}
            >
              {leaveTeamMutation.isPending ? 'Leaving...' : 'Leave Team'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
